import { useState, useEffect, useMemo } from "react";

const DEFAULT_ROTATE_MS = 15000;
const MIN_ROTATE_MS = 1000;

/**
 * Parses rotateMs from URL param; clamps invalid values.
 * @param {string} param - Raw URL param (e.g. from useParams)
 * @returns {number}
 */
export function parseRotateMs(param) {
  if (param == null || param === "") return DEFAULT_ROTATE_MS;
  const n = parseInt(param, 10);
  if (Number.isNaN(n) || n < MIN_ROTATE_MS) return DEFAULT_ROTATE_MS;
  return n;
}

/**
 * Parses locale param: short format "sk", "en" or "sk_en" (underscore-separated).
 * Deduplicates while preserving order.
 * @param {string} param - Raw URL param (e.g. "sk", "sk_en")
 * @returns {string[]} Short codes, e.g. ["sk", "en"]
 */
export function parseLocaleParam(param) {
  if (param == null || param === "") return [];
  const parts = String(param)
    .split("_")
    .map((s) => s.trim())
    .filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const p of parts) {
    if (!seen.has(p)) {
      seen.add(p);
      out.push(p);
    }
  }
  return out;
}

const SHORT_TO_FULL_FALLBACK = { sk: "sk-SK", en: "en-US", cs: "cs-CZ", de: "de-DE" };

/**
 * Maps short locale codes from URL to full locale codes used in menu data.
 * URL uses "sk", "en"; data keys are "sk-SK", "en-US".
 * For each short code: first try supportedKeys; if not found, use SHORT_TO_FULL_FALLBACK so URL "sk_en" always rotates.
 * @param {string[]} shortLocales - e.g. ["sk", "en"]
 * @param {string[]} supportedKeys - e.g. ["sk-SK", "en-US"] (may be empty when API doesn't send settings)
 * @returns {string[]} e.g. ["sk-SK", "en-US"]
 */
export function mapShortLocalesToFull(shortLocales, supportedKeys) {
  const result = [];
  for (const shortCode of shortLocales) {
    const fullFromKeys =
      Array.isArray(supportedKeys) &&
      supportedKeys.find((k) => k === shortCode || k.startsWith(shortCode + "-"));
    const full = fullFromKeys ?? SHORT_TO_FULL_FALLBACK[shortCode] ?? (shortCode.length <= 3 ? `${shortCode}-${String(shortCode).toUpperCase()}` : shortCode);
    if (full && !result.includes(full)) result.push(full);
  }
  if (result.length > 0) return result;
  if (Array.isArray(supportedKeys) && supportedKeys.length > 0) return [supportedKeys[0]];
  return ["sk-SK"];
}

/**
 * Filters locale list to only those present in JSON data.
 * Accepts short URL codes (sk, en) and maps them to full (sk-SK, en-US) using menu data keys.
 * @param {string[]} locales - Parsed locale list (short codes from URL)
 * @param {Object} menuData - JSON data (keys = supported full locales, e.g. "sk-SK", "en-US")
 * @returns {string[]} Full locale list for rotation
 */
export function filterToSupportedLocales(locales, menuData) {
  const keys = Object.keys(menuData || {});
  return mapShortLocalesToFull(locales.length > 0 ? locales : ["sk"], keys);
}

/**
 * Hook: aktuálny jazyk z nastavení (single alebo rotácia).
 * Jazyk a interval rotácie sa berú z nastavení, nie z URL.
 * @param {string[]} supportedLocales - Plné kódy z nastavení (e.g. ["sk-SK", "en-US"])
 * @param {number} rotateMs - Interval rotácie v ms z nastavení (0 = bez rotácie)
 * @returns {[string, number]} [currentLocale, rotateMs]
 */
export function useRotatingLocale(supportedLocales = [], rotateMs = 0) {
  const locales = useMemo(() => {
    const list = Array.isArray(supportedLocales) && supportedLocales.length > 0
      ? supportedLocales
      : ["sk-SK"];
    return [...list];
  }, [supportedLocales]);

  const [index, setIndex] = useState(0);
  const currentLocale = locales[index] || locales[0] || "sk-SK";
  const ms = Number(rotateMs) > 0 ? Number(rotateMs) : 0;

  useEffect(() => {
    if (locales.length <= 1 || ms <= 0) return undefined;
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % locales.length);
    }, ms);
    return () => clearInterval(id);
  }, [locales.length, ms]);

  return [currentLocale, ms];
}

export default useRotatingLocale;
